import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import {
  X, Shield, AlertTriangle, Phone, Users, FileText,
  Heart, Flame, Home, AlertOctagon, Loader2
} from "lucide-react";

const emergencyIcons = {
  medical_emergency: Heart,
  client_fall: AlertTriangle,
  injury: AlertTriangle,
  behavioral_crisis: AlertOctagon,
  fire: Flame,
  unable_to_enter: Home,
  unsafe_situation: Shield,
  other: AlertOctagon,
};

const emergencyLabels = {
  medical_emergency: "Medical Emergency",
  client_fall: "Client Fall",
  injury: "Injury",
  behavioral_crisis: "Behavioral Crisis",
  fire: "Fire",
  unable_to_enter: "Unable to Enter Home",
  unsafe_situation: "Unsafe Situation",
  other: "Other Emergency",
};

const actionLabels = {
  call_911: "Call 911",
  notify_office: "Notify Office",
  notify_emergency_contact: "Contact Emergency Contact",
  call_supervisor: "Call Supervisor",
  create_incident_report: "Create Incident Report",
};

export default function EmergencyModal({ visit, onClose, onSuccess }) {
  const [emergencyOptions, setEmergencyOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [details, setDetails] = useState("");
  const [actionsTaken, setActionsTaken] = useState([]);
  const [step, setStep] = useState(1); // 1: select type, 2: actions & details, 3: confirm

  useEffect(() => {
    loadEmergencyOptions();
  }, []);

  const loadEmergencyOptions = async () => {
    try {
      const { data } = await api.get("/caregiver-workspace/emergency-options");
      setEmergencyOptions(data);
    } catch (err) {
      console.error("Failed to load emergency options:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectType = (type) => {
    setSelectedType(type);
    setStep(2);
  };

  const toggleAction = (action) => {
    setActionsTaken(prev => 
      prev.includes(action) 
        ? prev.filter(a => a !== action)
        : [...prev, action]
    );
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Try to capture GPS for emergency (helps office respond)
      let gpsCoordinates = null;
      try {
        const position = await new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        });
        gpsCoordinates = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
      } catch (gpsErr) {
        console.warn("Could not get GPS for emergency:", gpsErr);
      }

      await api.post("/caregiver-workspace/quick-action", {
        action_type: "emergency",
        visit_id: visit?.id,
        client_id: visit?.client_id,
        emergency_type: selectedType,
        details: details,
        emergency_actions_taken: actionsTaken,
        gps_coordinates: gpsCoordinates,
      });
      
      onSuccess && onSuccess();
      onClose();
    } catch (err) {
      console.error("Failed to report emergency:", err);
      alert(err.response?.data?.detail || "Failed to report emergency");
    } finally {
      setSubmitting(false);
    }
  };

  const suggestedActions = emergencyOptions?.actions_by_type?.[selectedType] || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg mx-auto bg-white rounded-t-3xl sm:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-red-50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <Shield className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h2 className="font-semibold text-red-900">Emergency Assistance</h2>
              <p className="text-sm text-red-700">Select the type of emergency</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-red-100 transition-colors"
            data-testid="close-emergency-modal"
          >
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-red-500 animate-spin" />
            </div>
          ) : step === 1 ? (
            /* Step 1: Select Emergency Type */
            <div className="space-y-3" data-testid="emergency-type-selection">
              <p className="text-sm text-gray-600 mb-4">
                What type of emergency are you experiencing?
              </p>
              {emergencyOptions?.emergency_types?.map((type) => {
                const IconComponent = emergencyIcons[type] || AlertOctagon;
                return (
                  <button
                    key={type}
                    onClick={() => handleSelectType(type)}
                    className="w-full flex items-center gap-4 p-4 rounded-xl border border-gray-200 hover:border-red-300 hover:bg-red-50 transition-colors text-left"
                    data-testid={`emergency-type-${type}`}
                  >
                    <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
                      <IconComponent className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {emergencyLabels[type] || type}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : step === 2 ? (
            /* Step 2: Actions & Details */
            <div className="space-y-5" data-testid="emergency-details">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    {React.createElement(emergencyIcons[selectedType] || AlertOctagon, {
                      className: "w-5 h-5 text-red-600"
                    })}
                  </div>
                  <div className="font-semibold text-lg">
                    {emergencyLabels[selectedType]}
                  </div>
                </div>
              </div>

              {/* Suggested Actions */}
              {suggestedActions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Recommended Actions
                  </label>
                  <div className="space-y-2">
                    {suggestedActions.map((action) => (
                      <button
                        key={action}
                        onClick={() => toggleAction(action)}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                          actionsTaken.includes(action)
                            ? "border-red-500 bg-red-50"
                            : "border-gray-200 hover:border-red-300"
                        }`}
                        data-testid={`action-${action}`}
                      >
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                          actionsTaken.includes(action)
                            ? "border-red-500 bg-red-500"
                            : "border-gray-300"
                        }`}>
                          {actionsTaken.includes(action) && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <span className="font-medium">
                          {actionLabels[action] || action}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Details */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Details (optional)
                </label>
                <textarea
                  value={details}
                  onChange={(e) => setDetails(e.target.value)}
                  placeholder="Describe the situation..."
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-red-500 focus:ring-1 focus:ring-red-500 resize-none"
                  data-testid="emergency-details-input"
                />
              </div>

              {/* Important Notice */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <strong>Important:</strong> If this is a life-threatening emergency, 
                    call 911 directly. This form notifies the office but does not contact 
                    emergency services.
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50">
          {step === 1 ? (
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl border border-gray-300 font-medium text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 rounded-xl border border-gray-300 font-medium text-gray-700 hover:bg-gray-100 transition-colors"
                disabled={submitting}
              >
                Back
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 py-3 rounded-xl bg-red-600 text-white font-medium hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                data-testid="submit-emergency"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4" />
                    Report Emergency
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
